/**
 * Shared location display/translation for market header (static and interactive).
 * Used by buildSrpViewModel and MarketHeader to get translated location and normalized English form for background lookup.
 */

export type SupportedLocale = 'en' | 'fi' | 'sv';

const CITY_NAME_MAPPINGS: Record<string, { fi: string; sv: string; en: string }> = {
  Helsinki: { fi: 'Helsinki', sv: 'Helsingfors', en: 'Helsinki' },
  Tampere: { fi: 'Tampere', sv: 'Tammerfors', en: 'Tampere' },
  Turku: { fi: 'Turku', sv: 'Åbo', en: 'Turku' },
  Oulu: { fi: 'Oulu', sv: 'Uleåborg', en: 'Oulu' },
  Espoo: { fi: 'Espoo', sv: 'Esbo', en: 'Espoo' },
  Vantaa: { fi: 'Vantaa', sv: 'Vanda', en: 'Vantaa' },
  Lahti: { fi: 'Lahti', sv: 'Lahtis', en: 'Lahti' },
  Kuopio: { fi: 'Kuopio', sv: 'Kuopio', en: 'Kuopio' },
  Jyväskylä: { fi: 'Jyväskylä', sv: 'Jyväskylä', en: 'Jyväskylä' },
  Pori: { fi: 'Pori', sv: 'Björneborg', en: 'Pori' },
  Lappeenranta: { fi: 'Lappeenranta', sv: 'Villmanstrand', en: 'Lappeenranta' },
  Vaasa: { fi: 'Vaasa', sv: 'Vasa', en: 'Vaasa' },
  Kotka: { fi: 'Kotka', sv: 'Kotka', en: 'Kotka' },
  Joensuu: { fi: 'Joensuu', sv: 'Joensuu', en: 'Joensuu' },
  Hämeenlinna: { fi: 'Hämeenlinna', sv: 'Tavastehus', en: 'Hämeenlinna' },
  Seinäjoki: { fi: 'Seinäjoki', sv: 'Seinäjoki', en: 'Seinäjoki' },
  Rovaniemi: { fi: 'Rovaniemi', sv: 'Rovaniemi', en: 'Rovaniemi' },
  Kouvola: { fi: 'Kouvola', sv: 'Kouvola', en: 'Kouvola' },
  Porvoo: { fi: 'Porvoo', sv: 'Borgå', en: 'Porvoo' },
  Kokkola: { fi: 'Kokkola', sv: 'Karleby', en: 'Kokkola' },
  Mikkeli: { fi: 'Mikkeli', sv: 'S:t Michel', en: 'Mikkeli' },
  Rauma: { fi: 'Rauma', sv: 'Raumo', en: 'Rauma' },
  Hanko: { fi: 'Hanko', sv: 'Hangö', en: 'Hanko' },
  Kemi: { fi: 'Kemi', sv: 'Kemi', en: 'Kemi' },
};

const COUNTRY_NAME_MAPPINGS: Record<string, { fi: string; sv: string; en: string }> = {
  Finland: { fi: 'Suomi', sv: 'Finland', en: 'Finland' },
  Suomi: { fi: 'Suomi', sv: 'Finland', en: 'Finland' },
};

/**
 * Normalize location to English "City, Country" for background image lookup.
 */
export function getNormalizedLocationEnglish(location: string): string {
  if (!location) return location;
  const parts = location.includes(',')
    ? location.split(',').map((p) => p.trim())
    : location.split('_').map((p) => p.trim());
  if (parts.length < 2) {
    const cityName = parts[0]?.trim() ?? '';
    for (const [, variants] of Object.entries(CITY_NAME_MAPPINGS)) {
      if (
        cityName.toLowerCase() === variants.en.toLowerCase() ||
        cityName.toLowerCase() === variants.fi.toLowerCase() ||
        cityName.toLowerCase() === variants.sv.toLowerCase()
      ) {
        return `${variants.en}, Finland`;
      }
    }
    return `${cityName}, Finland`;
  }
  const cityPart = parts[0].trim();
  const countryPart = parts[1].trim();
  let translatedCity = cityPart;
  for (const [, variants] of Object.entries(CITY_NAME_MAPPINGS)) {
    if (
      cityPart.toLowerCase() === variants.en.toLowerCase() ||
      cityPart.toLowerCase() === variants.fi.toLowerCase() ||
      cityPart.toLowerCase() === variants.sv.toLowerCase()
    ) {
      translatedCity = variants.en;
      break;
    }
  }
  return `${translatedCity}, Finland`;
}

/**
 * Translate location for display in the given locale.
 */
export function getTranslatedLocationForHeader(location: string, locale: SupportedLocale): string {
  if (!location) return location;
  const parts = location.includes(',')
    ? location.split(',').map((p) => p.trim())
    : location.split('_').map((p) => p.trim());
  if (parts.length < 2) {
    const cityName = parts[0]?.trim() ?? '';
    for (const [, variants] of Object.entries(CITY_NAME_MAPPINGS)) {
      if (
        cityName.toLowerCase() === variants.en.toLowerCase() ||
        cityName.toLowerCase() === variants.fi.toLowerCase() ||
        cityName.toLowerCase() === variants.sv.toLowerCase()
      ) {
        return variants[locale];
      }
    }
    return cityName;
  }
  const cityPart = parts[0].trim();
  const countryPart = parts[1].trim();
  let translatedCity = cityPart;
  for (const [, variants] of Object.entries(CITY_NAME_MAPPINGS)) {
    if (
      cityPart.toLowerCase() === variants.en.toLowerCase() ||
      cityPart.toLowerCase() === variants.fi.toLowerCase() ||
      cityPart.toLowerCase() === variants.sv.toLowerCase()
    ) {
      translatedCity = variants[locale];
      break;
    }
  }
  let translatedCountry = countryPart;
  for (const [, variants] of Object.entries(COUNTRY_NAME_MAPPINGS)) {
    if (
      countryPart.toLowerCase() === variants.en.toLowerCase() ||
      countryPart.toLowerCase() === variants.fi.toLowerCase() ||
      countryPart.toLowerCase() === variants.sv.toLowerCase()
    ) {
      translatedCountry = variants[locale];
      break;
    }
  }
  return `${translatedCity}, ${translatedCountry}`;
}
